/**
 * ThreadV5MobileShell — FXC-8939.
 *
 * Below ~768px the desktop topbar + left nav rail are replaced by a
 * mobile-first shell: a compact header with the shared top Start menu. The
 * surface + drawers + focus layer render full-bleed beneath it as `children`,
 * so the same workbench data,
 * plot-point handler, and provenance focus layer drive both shells —
 * desktop and the anonymous /public share recipient alike.
 *
 * The menu preserves the full global destination and layout inventory on
 * narrow screens without reserving a bottom or side navigation band.
 */

import type { ReactNode } from "react";
import { ThemeToggle } from "../../components/toolbar/ThemeToggle";

interface ThreadV5MobileShellProps {
  projectName: string;
  readOnly: boolean;
  temporalReplayActive?: boolean;
  navigationMenu: ReactNode;
  onSignIn: () => void;
  children: ReactNode;
}

export function ThreadV5MobileShell({
  projectName,
  readOnly,
  temporalReplayActive = false,
  navigationMenu,
  onSignIn,
  children,
}: ThreadV5MobileShellProps) {
  return (
    <div className="v5-mobile-shell" data-testid="v5-mobile-shell">
      <header className="v5-mobile-header">
        <div className="v5-mobile-brand">
          {navigationMenu}
          <span className="v5-mobile-title" title={projectName}>{projectName}</span>
        </div>
        {temporalReplayActive ? (
          <span className="v5-mobile-temporal-replay" data-testid="v5-mobile-exact-temporal-replay">Exact replay</span>
        ) : null}
        {/* FXC-9000: theme toggle reachable in the mobile shell too. */}
        <ThemeToggle />
        {readOnly ? (
          <button className="v5-mobile-signin" type="button" data-testid="v5-mobile-signin" onClick={onSignIn}>
            Sign in
          </button>
        ) : null}
      </header>

      {/* Surfaces own the single page-level <main> landmark. The shell is layout only. */}
      <div className="v5-mobile-content">{children}</div>
    </div>
  );
}
